import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { SearchSelect } from "../ui/SearchSelect";
import { ToggleSwitch } from "../ui/ToggleSwitch";
import { CompanyFormModal } from "../parties/CompanyFormModal";
import { DocChip } from "./DocChip";
import { FilePreviewModal } from "./FilePreviewModal";
import { ManualLineBuilder } from "./ManualLineBuilder";
import { LotSubstitutePicker } from "./LotSubstitutePicker";
import { useIsMobile } from "../../lib/useIsMobile";
import "./ShipmentDetailDrawer.css";
import { trpc } from "../../lib/trpc";
import { yuklemeFormuUrunleri, formMiktari, type YuklemeUrunu } from "../../lib/loadingFormItems";

interface ShipmentDetailProps {
  open: boolean;
  shipmentId: string | null;
  onClose: () => void;
  /** Plan silindi (çekmece kendini kapatır) — başarı mesajı üst bileşenin toast'ına gider; sessiz başarı yok. */
  onDeleted?: (message: string) => void;
}

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  draft: { cls: "b-warn", label: "Hazırlanıyor" },
  confirmed: { cls: "b-success", label: "Sevk Edildi" },
  cancelled: { cls: "", label: "İptal" },
};

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
function fmtMoney(n: number): string {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }).format(n);
}
/** TR sayı biçimi: 1.234 / 1.234,56 / 12.500 → noktalar binlik, virgül ondalık (inventory.ts parseTrNumber ile aynı). */
function parseTrNumber(raw: string): number {
  const s = raw.trim();
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) return Number(s.replace(/\./g, "").replace(",", "."));
  if (s.includes(",")) return Number(s.replace(/\./g, "").replace(",", "."));
  return Number(s);
}
/** Sayıyı TR girişine hazırla: ondalık virgül, binlik ayraç yok (24360.5 → "24360,5"). String(n) ile doldurmak yasak — parseTrNumber noktayı binlik sanıp 10x bozar. */
function toTrInput(n: number): string {
  return String(n).replace(".", ",");
}
/** Print HTML kaçışı (TraceViewerDrawer.escHtml ile aynı). */
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] ?? c));
}
function fmtDate(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(d));
}

interface ShipFromProfile {
  name: string; logoUrl: string | null; address: string | null;
  taxOffice: string | null; taxNumber: string | null; phone: string | null; email: string | null;
}

// Yükleme Formu yazdır — çok-müşterili (manuel çoklu-sipariş) sevkiyatta HER MÜŞTERİ İÇİN AYRI FORM
// (aynı şoför/araç/nakliye bilgisi paylaşılır + "Çoklu Yükleme" ibaresi). Tek müşteride tek form (eski davranış).
// Her form: Sayfa 1 yükleme özeti (parti no YOK, kritik bilgiler vurgulu) + Sayfa 2 izlenebilirlik eki.
function printIrsaliye(s: NonNullable<ReturnType<typeof useShipmentData>>, profile?: ShipFromProfile) {
  const w = window.open("", "_blank", "width=860,height=1000");
  if (!w) return;

  // ── Müşteri bazında grupla: sevkiyat satırı → siparişi → müşterisi. Siparişsiz (stok) satırlar ayrı kovada. ──
  // Form (Sayfa 1 + Sayfa 2) FİİLEN YÜKLENECEK sevkiyat satırlarından kurulur: pick list'te "yüklenmeyecek"
  // (excluded) işaretli partiler Sayfa 1 toplamına GİRMEZ. MANUEL sevkiyatta ayrıca siparişin
  // HİÇ PARTİSİ OLMAYAN kalemleri de sipariş miktarıyla listeye eklenir (aşağıda) — kâğıt
  // siparişin tamamını göstermeli, üretilmemiş kalem unutulmamalı.
  const orderByCode = new Map(s.orders.map((o) => [o.code, o] as const));
  type CustGroup = { key: string; name: string; taxNumber: string | null; address: string | null; orderCodes: Set<string>; shipLines: typeof s.lines };
  const custGroups = new Map<string, CustGroup>();
  const groupFor = (orderCode: string | null): CustGroup => {
    const ord = orderCode ? orderByCode.get(orderCode) : undefined;
    const key = ord?.customerCode ?? ord?.customerName ?? "__stok__";
    let g = custGroups.get(key);
    if (!g) { g = { key, name: ord?.customerName ?? "Stok / Siparişsiz", taxNumber: ord?.customerTaxNumber ?? null, address: ord?.customerAddress ?? null, orderCodes: new Set(), shipLines: [] }; custGroups.set(key, g); }
    return g;
  };
  for (const l of s.lines) { const g = groupFor(l.orderCode); g.shipLines.push(l); if (l.orderCode) g.orderCodes.add(l.orderCode); }
  // Yalnız EN AZ BİR yüklenecek (non-excluded) satırı olan gruplar form üretir — tüm partileri "yüklenmeyecek"
  // işaretli grup BOŞ form + yanlış "ÇOKLU YÜKLEME" atfı yaratmasın (fiili yükle tutarlı).
  const groups: CustGroup[] = [...custGroups.values()].filter((g) => g.shipLines.some((l) => !l.excluded));
  // GERÇEK müşteriler (stok/siparişsiz kovası müşteri DEĞİL). Çok-form kararı yalnız gerçek müşteri sayısına bakar.
  const realGroups = groups.filter((g) => g.key !== "__stok__");
  // İki+ GRUP (müşteri VEYA __stok__ kovası) varsa her grup AYRI form — böylece müşterisiz (stock_replenish /
  // müşterisi silinmiş) siparişin kalemleri gerçek müşterinin formuna KARIŞMAZ (yanlış atıf). Tek grupsa tek form.
  const isMulti = groups.length > 1;
  // Çok-grupta her grup ayrı form (stok kovası dahil kendi formu). Değilse: TÜM satırlar tek formda (eski davranış).
  const formGroups: CustGroup[] = isMulti
    ? groups
    : [{
        key: "__single__",
        name: realGroups[0]?.name ?? "—", // gerçek müşteri yoksa (hepsi stoğa) eski "—"
        taxNumber: realGroups[0]?.taxNumber ?? null,
        address: realGroups[0]?.address ?? null,
        orderCodes: new Set(groups.flatMap((g) => [...g.orderCodes])),
        shipLines: s.lines,
      }];

  // ── Paylaşılan alanlar (araç/şoför/nakliye/kantar/antet) — her formda birebir aynı. ──
  const line = (v?: string | null, hi = false) => v ? `<b class="${hi ? "hi" : ""}">${esc(v)}</b>` : `<span class="fill">&nbsp;</span>`;
  const cb = (checked: boolean) => `<span class="cbx">${checked ? "✕" : "&nbsp;"}</span>`;
  const tarih = s.plannedDate ? fmtDateOnly(s.plannedDate) : s.shippedAt ? fmtDateOnly(s.shippedAt) : fmtDateOnly(new Date().toISOString());
  const logo = profile?.logoUrl ? new URL(profile.logoUrl, window.location.origin).href : null;
  const firmaAlt = esc([profile?.address, [profile?.taxOffice ? `VD: ${profile.taxOffice}` : "", profile?.taxNumber ? `VKN: ${profile.taxNumber}` : ""].filter(Boolean).join(" · "), profile?.phone ? `Tel: ${profile.phone}` : "", profile?.email ?? ""].filter(Boolean).join(" · "));
  const freightLabel = s.freightParty === "firma" ? "Firmaya Aittir" : s.freightParty === "karsi" ? "Karşıya Aittir" : null;
  const freightAmount = s.freightParty === "firma" && s.freightAmount != null
    ? `${fmtMoney(s.freightAmount)} ${s.freightCurrency ?? ""}` : null;
  const mkHeader = (badge: boolean) => `
    <div class="head">
      ${logo ? `<img src="${logo}" alt="Logo">` : "<div style='width:84px'></div>"}
      <div class="firma">
        <div class="fname">${profile?.name ? esc(profile.name) : "&nbsp;"}</div>
        <div class="ftitle">YÜKLEME FORMU${badge ? `<span class="mbadge">ÇOKLU YÜKLEME</span>` : ""}</div>
      </div>
      <table class="meta"><tbody>
        <tr><td>TARİH</td><td class="mono">${tarih}</td></tr>
        <tr><td>SAAT</td><td class="mono">${s.plannedTime ? esc(s.plannedTime) : "&nbsp;"}</td></tr>
        <tr><td>FORM NO</td><td class="mono">${esc(s.code)}</td></tr>
      </tbody></table>
    </div>`;
  const sharedBlocks = `
    <div class="sect">ŞOFÖR / ARAÇ BİLGİLERİ</div>
    <div class="grid2">
      <div class="kv"><span class="k">Ad-Soyad</span>${line(s.driverName, true)}</div>
      <div class="kv"><span class="k">Araç Plaka No</span>${line(s.vehiclePlate, true)}</div>
      <div class="kv"><span class="k">Dorse Plaka No</span>${line(s.trailerPlate, true)}</div>
      <div class="kv"><span class="k">T.C.</span>${line(s.driverTc)}</div>
      <div class="kv"><span class="k">Telefon</span>${line(s.driverPhone)}</div>
    </div>
    <div class="kv" style="margin-top:8px"><span class="k" style="min-width:auto">ARAÇ İÇ TEMİZLİĞİ SEVKİYATA UYGUNDUR:</span>
      &nbsp;EVET ${cb(s.vehicleCleanOk === true)} &nbsp;&nbsp;HAYIR ${cb(s.vehicleCleanOk === false)}</div>

    <div class="sect">NAKLİYE BİLGİLERİ</div>
    <div class="grid2">
      ${s.carrier ? `<div class="kv"><span class="k">Taşıyıcı</span><b>${esc(s.carrier)}</b></div>` : ""}
      ${s.waybillNo ? `<div class="kv"><span class="k">İrsaliye No</span><b class="mono">${esc(s.waybillNo)}</b></div>` : ""}
      <div class="kv"><span class="k">Nakliye Tutarı</span>${line(freightAmount)}</div>
      <div class="kv"><span class="k">Ait Olduğu Taraf</span>${line(freightLabel)}</div>
    </div>

    <div class="sect">KANTAR / TARTIM</div>
    <div class="grid2">
      <div class="kv"><span class="k">Dolu Ağırlık (kg)</span>${line(s.fullWeightKg != null ? fmtQty(s.fullWeightKg) : null)}</div>
      <div class="kv"><span class="k">Boş Ağırlık (kg)</span>${line(s.emptyWeightKg != null ? fmtQty(s.emptyWeightKg) : null)}</div>
      <div class="kv"><span class="k">Net Çıkış (kg)</span>${s.fullWeightKg != null && s.emptyWeightKg != null ? `<b class="mono">${fmtQty(s.fullWeightKg - s.emptyWeightKg)}</b>` : line(null)}</div>
    </div>

    <div class="sig">
      <div><div class="lbl">YÜKLEME YAPAN</div><div class="area"></div></div>
      <div><div class="lbl">KONTROL EDEN</div><div class="area"></div></div>
      <div><div class="lbl">ONAYLAYAN</div><div class="area"></div></div>
    </div>
    ${firmaAlt ? `<div class="foot">${firmaAlt}</div>` : ""}`;

  // ── Her müşteri grubu için form gövdesi (Sayfa 1 + Sayfa 2). idx>0 yeni sayfada başlar. ──
  const sections = formGroups.map((g, idx) => {
    // Sayfa 1: FİİLEN YÜKLENECEK ürün özeti — pick list'te "yüklenmeyecek" (excluded) işaretli partiler HARİÇ,
    // ürün (itemCode+uom) bazında sevkiyat satırı miktarları toplanır. KOLİ SAYISI = kamyona fiilen yüklenen.
    // Tüm partileri hariç tutulan ürün burada görünmez (bilinçli karar). HİÇ partisi olmayan kalem
    // ise MANUEL sevkiyatta aşağıda sipariş kaleminden tohumlanır ("şu an hazır: 0" ile).
    // Anahtar = itemCode + uom: aynı ürün farklı birimlerde (ör. 100 KOLİ + 500 ADET) AYRI satır kalır.
    /**
     * ═══ KOLİ SAYISI: MANUEL PLANDA SİPARİŞİN TAMAMI ═══
     *
     * Kullanıcı kararı (5 Ağu 2026): "manuel yükleme olarak planlandığı zaman formda
     * 1.537 değil 2.700 yazılı olsun."
     *
     * Gerekçe operasyonel: manuel plan ÜRETİM SÜRERKEN açılıyor (form başlığındaki
     * "ÖN ONAYLI YÜKLEME — ÜRETİM DEVAM ETMEKTEDİR" ibaresi bunu söylüyor). Yükleme
     * ekibi kâğıda bakıp araca kaç koli koyacağını bilmeli; o an atanmış lot miktarı
     * üretim ilerledikçe değişir, SİPARİŞ MİKTARI değişmez. Hedef budur.
     *
     * OTOMATİK sevkiyatta davranış AYNEN KORUNUR (fiilen yüklenen) — orada plan
     * üretim bittikten sonra kuruluyor ve kâğıt sevk edilen malı belgeliyor.
     *
     * ⚠ MÜKERRER SAYIM TUZAĞI: aynı sipariş satırı BİRDEN ÇOK lotla karşılanabilir
     * (3 lot → 3 sevkiyat satırı, hepsi AYNI orderLineId). Sipariş miktarını her
     * satırda toplamak 2.700'ü 8.100 yapardı; bu yüzden orderLineId bazında TEK KEZ.
     */
    /**
     * Ürün listesi kurgusu SAF FONKSİYONDA: `lib/loadingFormItems.ts`.
     * Kural (hangi ürün / kaç adet) burada HTML dizesi içine gömülüyken test edilemiyordu;
     * "hiç partisi olmayan kalem de listede olmalı" gibi bir kuralın testsiz kalması, basılı
     * belgeye ve fiziksel yüklemeye dokunduğu için kabul edilebilir değildi.
     */
    const products = yuklemeFormuUrunleri({
      sevkSatirlari: g.shipLines.map((l) => ({
        orderLineId: l.orderLineId, itemCode: l.itemCode, itemName: l.itemName,
        uom: l.uom, qty: l.qty, orderedQty: l.orderedQty, excluded: l.excluded,
      })),
      // Bu formdaki siparişlerin TÜM kalemleri — sevkiyat satırı olmayanlar dahil.
      siparisKalemleri: [...g.orderCodes].flatMap((kod) => orderByCode.get(kod)?.kalemler ?? []),
      isManual: s.isManual,
    });
    const formQty = (u: YuklemeUrunu) => formMiktari(u, s.isManual);
    const totByUom = new Map<string, number>();
    for (const p of products) totByUom.set(p.uom, (totByUom.get(p.uom) ?? 0) + formQty(p));
    const totalLabel = [...totByUom.entries()].map(([uom, q]) => `${fmtQty(q)} ${esc(uom)}`).join(" + ") || "—";
    const rows = products.map((p, i) => {
      const yazilan = formQty(p);
      // Hedef ile o an hazır olan FARKLIYSA sessiz kalma: kâğıt 2.700 derken depoda
      // 1.537 hazırsa yükleme ekibi bunu bilmeli (belge kendi kendini açıklasın).
      const fark = s.isManual && p.hedefSatirlari.size > 0 && Math.abs(p.hedef - p.hazir) > 0.0001;
      return `
      <tr>
        <td class="num c">${i + 1}</td>
        <td><b>${esc(p.name)}</b>${fark ? `<div class="alt">şu an hazır: ${fmtQty(p.hazir)} ${esc(p.uom)}</div>` : ""}</td>
        <td class="num koli">${fmtQty(yazilan)} ${esc(p.uom)}</td>
        <td class="num"></td><td class="num"></td>
      </tr>`;
    }).join("");
    const emptyRows = Array.from({ length: Math.max(0, 3 - products.length) }, () =>
      `<tr><td class="num c">&nbsp;</td><td></td><td></td><td></td><td></td></tr>`).join("");

    // Sayfa 2: lot bazında izlenebilirlik (aynı lot birden çok lokasyondan gelirse miktarı topla).
    // "Yüklenmeyecek" (excluded) partiler burada da GÖRÜNMEZ — fiilen yüklenen yükle tutarlı.
    const lotMap = new Map<string, { itemName: string; qty: number; uom: string; production: (typeof s.lines)[number]["production"] }>();
    for (const l of g.shipLines) {
      if (l.excluded) continue;
      const e = lotMap.get(l.lotCode);
      if (e) e.qty += l.qty;
      else lotMap.set(l.lotCode, { itemName: l.itemName, qty: l.qty, uom: l.uom, production: l.production });
    }
    const lotRows = [...lotMap.entries()].map(([lotCode, e], i) => {
      const p = e.production;
      return `
      <tr>
        <td class="num c">${i + 1}</td>
        <td class="mono b">${esc(lotCode)}</td>
        <td>${esc(e.itemName)}</td>
        <td class="num">${fmtQty(e.qty)} ${esc(e.uom)}</td>
        <td class="mono">${esc(p.batchCode ?? p.woCode ?? "—")}</td>
        <td>${p.lineName ? esc(p.lineName) : "—"}</td>
        <td class="mono">${fmtDate(p.producedAt)}</td>
        <td>${p.producedBy ? esc(p.producedBy) : "—"}</td>
        <td class="c">${cb(false)}</td>
      </tr>`;
    }).join("");

    // Palet/kat sevkiyat GENELİ (araç toplamı) → tek müşteriye atfedilemez; çok-müşteride boş bırak (operatör el yazısı).
    const palletCell = !isMulti && s.palletCount != null ? fmtQty(s.palletCount) : "";
    const layerCell = !isMulti && s.layerCount != null ? fmtQty(s.layerCount) : "";
    // Bu formda FİİLEN YÜKLENEN (non-excluded) satırların sipariş kodları — hem banner hem ctx yalnız buna bakar.
    // (Tüm satırları "yüklenmeyecek" işaretli bir sipariş bu formda yüklenmiyor sayılır → yanlış-pozitif
    //  "ÜRETİM DEVAM EDİYOR" banner'ı ya da ctx sipariş-kodu atfı oluşmaz.)
    const loadedCodes = new Set<string>();
    for (const l of g.shipLines) if (!l.excluded && l.orderCode) loadedCodes.add(l.orderCode);
    const ctx = isMulti
      ? `<div class="mctx">Bu araçta birden çok grubun (müşteri/stok) yükü taşınıyor · bu form ${idx + 1}/${formGroups.length}: <b>${esc(g.name)}</b>${loadedCodes.size ? ` · ${[...loadedCodes].map(esc).join(", ")}` : ""}</div>`
      : "";
    // ÖN ONAYLI YÜKLEME: bu formda YÜKLENEN siparişlerden biri henüz üretimde ise (durum 'ready'/'shipped' DEĞİL) →
    // üretim tamamlanmadan hazırlanan erken/ön-onaylı yükleme → formda uyarı notu basılır.
    const preApproved = [...loadedCodes].some((code) => {
      const st = orderByCode.get(code)?.status;
      return st === "confirmed" || st === "in_production";
    });
    const preAppBanner = preApproved ? `<div class="preapp">ÖN ONAYLI YÜKLEME — ÜRETİM DEVAM ETMEKTEDİR!</div>` : "";

    return `
    <div class="form"${idx > 0 ? ' style="page-break-before:always"' : ""}>
      ${mkHeader(isMulti)}
      ${preAppBanner}
      ${ctx}
      <div class="sect">MÜŞTERİ BİLGİLERİ</div>
      <div class="grid2">
        <div class="kv"><span class="k">Unvan</span>${line(g.name, true)}</div>
        <div class="kv"><span class="k">Vergi Kimlik No</span>${line(g.taxNumber)}</div>
        <div class="kv"><span class="k">Adres</span>${line(g.address)}</div>
        <div class="kv"><span class="k">Vergi Dairesi</span>${line(null)}</div>
      </div>

      <div class="sect">YÜKLENEN ÜRÜNLER${isMulti ? ` — ${esc(g.name)}` : ""}</div>
      <table class="dt">
        <thead><tr><th style="width:26px">#</th><th>ÜRÜN ADI</th><th style="width:110px">KOLİ SAYISI</th><th style="width:100px">PALET SAYISI</th><th style="width:90px">KAT SAYISI</th></tr></thead>
        <tbody>${rows}${emptyRows}</tbody>
        <tfoot><tr><td colspan="2" style="text-align:right">TOPLAM</td><td class="num koli">${totalLabel}</td><td class="num">${palletCell}</td><td class="num">${layerCell}</td></tr></tfoot>
      </table>

      ${sharedBlocks}

      <div class="page2">
        ${mkHeader(isMulti)}
        <div class="p2title">EK — ÜRETİM / İZLENEBİLİRLİK DETAYI${isMulti ? ` — ${esc(g.name)}` : ""}</div>
        <div class="p2sub">Bu formdaki tüm partilerin üretim kaydı (lot bazında). Form No: <b class="mono">${esc(s.code)}</b> · ${lotMap.size} parti</div>
        <table class="dt">
          <thead><tr>
            <th style="width:26px">#</th><th style="width:118px">LOT NO</th><th>ÜRÜN</th><th style="width:80px">MİKTAR</th>
            <th style="width:118px">ÜRETİM PARTİSİ</th><th style="width:90px">ÜRETİM HATTI</th><th style="width:104px">ÜRETİM ZAMANI</th><th style="width:100px">ÜRETEN</th><th style="width:76px" class="c">KONTROL</th>
          </tr></thead>
          <tbody>${lotRows}</tbody>
        </table>
      </div>
    </div>`;
  }).join("");

  w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Yükleme Formu ${esc(s.code)}${isMulti ? ` · ${formGroups.length} form` : ""}</title>
    <style>
    *{box-sizing:border-box}
    html,body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    body{font-family:'Segoe UI',Arial,sans-serif;color:#18181b;padding:26px;font-size:11.5px;line-height:1.45}
    .mono{font-family:'Consolas','Courier New',monospace}
    .b{font-weight:700}.c{text-align:center}
    .head{display:flex;align-items:center;gap:20px;border-bottom:3px solid #14253e;padding-bottom:12px}
    .head img{height:62px;max-width:170px;object-fit:contain}
    .firma{flex:1;text-align:left}
    .fname{font-size:27px;font-weight:800;letter-spacing:0.01em;color:#14253e;line-height:1.05}
    .ftitle{display:block;margin-top:3px;font-size:13px;font-weight:700;letter-spacing:0.2em;color:#475569}
    .mbadge{display:inline-block;background:#b45309;color:#fff;font-size:8.5px;font-weight:800;letter-spacing:0.14em;padding:2px 8px;border-radius:3px;margin-left:10px;vertical-align:middle}
    .mctx{margin-top:12px;font-size:10px;color:#92400e;font-weight:700;letter-spacing:0.02em;background:#fef3c7;border-left:3px solid #b45309;padding:6px 10px;border-radius:3px}
    .preapp{margin:10px 0 0;text-align:center;font-size:13px;font-weight:800;letter-spacing:0.06em;color:#b91c1c;background:#fef2f2;border:2px solid #b91c1c;padding:8px 12px;border-radius:4px}
    .meta{border-collapse:collapse;font-size:10.5px;min-width:180px}
    .meta td{border:1px solid #cbd5e1;padding:3px 8px}
    .meta td:first-child{font-weight:700;background:#eef2f7;color:#334155;width:64px}
    .meta td:last-child{font-weight:700;min-width:110px}
    .sect{background:#14253e;color:#fff;font-weight:700;font-size:10px;letter-spacing:0.1em;padding:4px 10px;border-radius:3px;margin:16px 0 8px}
    .grid2{display:grid;grid-template-columns:1fr 1fr;column-gap:28px;row-gap:4px}
    .kv{display:flex;gap:8px;font-size:11.5px;align-items:baseline}
    .kv .k{min-width:128px;font-weight:600;color:#52525b;font-size:10.5px;text-transform:uppercase;letter-spacing:0.03em}
    .kv .hi{font-size:13px}
    .fill{display:inline-block;border-bottom:1px dotted #94a3b8;min-width:200px}
    table.dt{width:100%;border-collapse:collapse;margin-top:6px}
    .dt th,.dt td{border:1px solid #94a3b8;padding:6px 8px;font-size:10.5px;text-align:left;vertical-align:middle}
    .dt th{background:#eef2f7;color:#14253e;font-weight:700;text-align:center;font-size:9.5px;letter-spacing:0.05em}
    .dt tbody tr:nth-child(even) td{background:#fafafa}
    .dt td.num{text-align:right;font-family:'Consolas','Courier New',monospace;white-space:nowrap}
    .dt td.koli,.dt tfoot td.koli{font-size:14px;font-weight:800;color:#14253e}
    /* Ürün adının altındaki ikincil bilgi ("şu an hazır: …") — KOLİ SAYISI hedefi
       gösterirken depoda o an ne olduğunu söyler; kâğıt kendi kendini açıklasın. */
    .dt td .alt{font-size:9px;font-weight:600;color:#b45309;margin-top:2px}
    .dt tfoot td{font-weight:800;background:#eef2f7;font-size:11px}
    .cbx{display:inline-block;width:16px;height:14px;border:1.5px solid #14253e;text-align:center;font-weight:700;line-height:13px;margin:0 4px}
    .sig{margin-top:30px;display:flex;gap:16px}
    .sig>div{flex:1;border:1px solid #94a3b8;border-radius:4px;overflow:hidden}
    .sig .lbl{background:#eef2f7;color:#14253e;font-weight:700;font-size:9.5px;letter-spacing:0.08em;text-align:center;padding:4px}
    .sig .area{height:52px}
    .foot{margin-top:20px;padding-top:8px;border-top:1px solid #d4d4d8;font-size:9px;color:#64748b;text-align:center}
    .page2{page-break-before:always;padding-top:6px}
    .p2title{font-size:12.5px;font-weight:800;letter-spacing:0.08em;color:#14253e;margin:14px 0 2px}
    .p2sub{font-size:9.5px;color:#64748b;margin-bottom:6px}
    @media print{body{padding:6px}}
    </style></head><body>
    ${sections}
    </body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 350);
}

function fmtDateOnly(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}

// get data hook (tipini çıkarmak için)
function useShipmentData(id: string | null, enabled: boolean) {
  const q = trpc.shipment.get.useQuery({ id: id! }, { enabled: enabled && !!id });
  return q.data;
}
type ShipmentData = NonNullable<ReturnType<typeof useShipmentData>>;


/**
 * TAMAMLANAN sevkiyat özeti — sevk edildikten sonra çekmecenin en üstünde (Yükleme Formu butonunun hemen
 * altında) tüm künye tek kartta; kullanıcı SOL RAYDAKİ ADIMLARA GİRMEDEN bilgiye ulaşır.
 */
function ShipmentSummaryCard({ s }: { s: ShipmentData }) {
  const net = s.fullWeightKg != null && s.emptyWeightKg != null ? s.fullWeightKg - s.emptyWeightKg : null;
  // Yüklenen miktar: yalnız sevke giren (hariç tutulmayan) satırlar, birim bazında toplanır.
  const byUom = new Map<string, number>();
  let lineCount = 0;
  for (const l of s.lines) {
    if (l.excluded) continue;
    lineCount++;
    byUom.set(l.uom, (byUom.get(l.uom) ?? 0) + l.qty);
  }
  const qtyLabel = [...byUom.entries()].map(([u, q]) => `${fmtQty(q)} ${u}`).join(" + ") || "—";
  const cleanLabel = s.vehicleCleanOk == null ? null : s.vehicleCleanOk ? "Evet" : "Hayır";
  const freightLabel = s.freightParty === "firma" ? "Firmaya Aittir" : s.freightParty === "karsi" ? "Karşıya Aittir" : null;
  const freightAmt = s.freightParty === "firma" && s.freightAmount != null ? `${fmtMoney(s.freightAmount)} ${s.freightCurrency ?? ""}` : null;

  const it = (label: string, value: React.ReactNode, mono?: boolean) => (
    <div className="shp-sum-it">
      <span className="k">{label}</span>
      <span className={`v${mono ? " mono" : ""}`}>{value == null || value === "" ? <i className="empty">—</i> : value}</span>
    </div>
  );

  return (
    <div className="d-card shp-sum">
      <div className="d-card-head">
        <div className="left">
          <span className="t">Sevkiyat Özeti</span>
          <span className="s">tamamlandı · tüm bilgiler tek bakışta</span>
        </div>
      </div>
      <div className="d-card-body">
        <div className="shp-sum-grid">
          {it("Sipariş", s.orders.length > 1 ? s.orders.map((o) => o.code).join(", ") : s.orderCode, true)}
          {it("Müşteri", s.customerName)}
          {it("Sevk Tarihi", s.shippedAt ? fmtDate(s.shippedAt) : null, true)}
          {it("Planlanan", [s.plannedDate ? fmtDateOnly(s.plannedDate) : null, s.plannedTime].filter(Boolean).join(" · ") || null, true)}
          {it("Taşıyıcı", s.carrier)}
          {it("Araç Plakası", s.vehiclePlate, true)}
          {it("Dorse Plakası", s.trailerPlate, true)}
          {it("Araç İç Temizliği", cleanLabel)}
          {it("Sürücü", s.driverName)}
          {it("Sürücü TC", s.driverTc, true)}
          {it("Sürücü Telefonu", s.driverPhone, true)}
          {it("Palet Sayısı", s.palletCount != null ? fmtQty(s.palletCount) : null, true)}
          {it("Kat Sayısı", s.layerCount != null ? fmtQty(s.layerCount) : null, true)}
          {it("Dolu Kantar", s.fullWeightKg != null ? `${fmtQty(s.fullWeightKg)} kg` : null, true)}
          {it("Boş Kantar", s.emptyWeightKg != null ? `${fmtQty(s.emptyWeightKg)} kg` : null, true)}
          {it("Net Çıkış", net != null ? `${fmtQty(net)} kg` : null, true)}
          {it("Nakliye", freightLabel)}
          {it("Nakliye Tutarı", freightAmt, true)}
          {it("İrsaliye No", s.waybillNo, true)}
          {it("Yüklenen", `${lineCount} satır · ${qtyLabel}`, true)}
          {it("Evraklar", s.documents.length > 0 ? `${s.documents.length} dosya` : null, true)}
        </div>
        {s.customerAddress ? (
          <div className="shp-sum-addr">
            <span className="k">Teslimat Adresi</span>
            <span className="v">{s.customerAddress}</span>
          </div>
        ) : null}
        {s.notes ? (
          <div className="shp-sum-addr">
            <span className="k">Not</span>
            <span className="v">{s.notes}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Sevkiyat planı / evrak kartı — taslakta düzenlenebilir alanlar + dosya yükleme; onaylıda salt-okunur.
 * WIZARD: `step` aktif adımı belirtir; adım grupları KOŞULLU RENDER EDİLMEZ, CSS ile gizlenir (`shp-hide`)
 * — aksi halde adım değiştirince girilmiş ama HENÜZ KAYDEDİLMEMİŞ form verisi unmount ile uçardı.
 * `showReq` = zorunlu alan yıldızı/uyarısı gösterilsin mi (yalnız taslakta anlamlı).
 */
function ShipmentEvrakCard({ s, step, showReq, onKaydedilmemis }: {
  s: ShipmentData; step: string; showReq: boolean;
  /** Kaydedilmemiş plan içeriğinin etiketleri ("" = form kayıtla aynı). Üst bileşenin "Boş Planı Sil" kararı için. */
  onKaydedilmemis?: (ozet: string) => void;
}) {
  const qc = useQueryClient();
  const editable = s.status === "draft";
  const [plannedDate, setPlannedDate] = useState("");
  const [carrier, setCarrier] = useState("");
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [trailerPlate, setTrailerPlate] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [driverTc, setDriverTc] = useState("");
  const [waybillNo, setWaybillNo] = useState("");
  const [docs, setDocs] = useState<string[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  /** Sürükleme görsel geri bildirimi + bırakma/yapıştırma hataları (sessiz başarısızlık YOK). */
  const [dragOver, setDragOver] = useState(false);
  const [dropHata, setDropHata] = useState<string | null>(null);
  // Yükleme Formu alanları
  const [plannedTime, setPlannedTime] = useState("");
  const [palletCount, setPalletCount] = useState("");
  const [layerCount, setLayerCount] = useState("");
  // Kantar (tartım): dolu/boş araç ağırlığı — net çıkış kg = dolu − boş (canlı gösterilir).
  const [fullWeight, setFullWeight] = useState("");
  const [emptyWeight, setEmptyWeight] = useState("");
  const [weightErr, setWeightErr] = useState<string | null>(null);
  const [vehicleClean, setVehicleClean] = useState<"" | "yes" | "no">("");
  const [freightParty, setFreightParty] = useState<"" | "firma" | "karsi">("");
  const [freightAmount, setFreightAmount] = useState("");
  const [freightCurrency, setFreightCurrency] = useState<"TRY" | "USD" | "EUR" | "GBP">("TRY");
  const [amountErr, setAmountErr] = useState<string | null>(null);
  const updateMut = trpc.shipment.update.useMutation();
  // Kaydet geri bildirimi: başarıda kısa süreli "kaydedildi" banner'ı (hata banner'ının simetriği).
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef<number | null>(null);
  useEffect(() => () => { if (savedTimer.current) window.clearTimeout(savedTimer.current); }, []);
  const uploadMut = trpc.shipment.uploadDocument.useMutation();
  const removeMut = trpc.shipment.removeDocument.useMutation();

  // Kayıtlı taşıyıcı (nakliyeci rolü) + sürücü seçicileri — seçince bilgiler otomatik dolar (snapshot).
  const [carrierPick, setCarrierPick] = useState<string | null>(null);
  const [driverPick, setDriverPick] = useState<string | null>(null);
  const [addCarrierOpen, setAddCarrierOpen] = useState(false);
  const [addDriverOpen, setAddDriverOpen] = useState(false);
  const [editDriver, setEditDriver] = useState<string | null>(null); // düzenlenecek sürücü-cari id'si
  const carrierQ = trpc.company.list.useQuery({ page: 1, pageSize: 100, role: "carrier", isActive: true }, { enabled: editable }); // company.list pageSize max=100
  const driverQ = trpc.company.list.useQuery({ page: 1, pageSize: 100, role: "driver", isActive: true }, { enabled: editable }); // sürücü artık Cari (isDriver rolü)
  const attachMut = trpc.shipment.attachDriverDocuments.useMutation();
  const carrierOpts = (carrierQ.data?.rows ?? []).map((c) => ({ value: c.id, label: c.name, desc: c.taxNumber ? `VN ${c.taxNumber}` : undefined, tail: c.city ?? undefined }));
  const driverOpts = (driverQ.data?.rows ?? []).map((d) => ({ value: d.id, label: d.name, desc: d.tc ? `TC ${d.tc}` : undefined, tail: d.phone ?? undefined }));

  function pickCarrier(id: string | null) {
    setCarrierPick(id);
    const c = id ? carrierQ.data?.rows.find((r) => r.id === id) : null;
    if (c) setCarrier(c.name);
  }
  const [attachedDriverIds, setAttachedDriverIds] = useState<Set<string>>(new Set()); // aynı sürücüyü tekrar seçince mükerrer kopya olmasın
  function pickDriver(id: string | null) {
    setDriverPick(id);
    const d = id ? driverQ.data?.rows.find((r) => r.id === id) : null;
    if (d) {
      setDriverName(d.name); setDriverTc(d.tc ?? ""); setDriverPhone(d.phone ?? "");
      // Sürücünün kayıtlı evraklarını sevkiyata otomatik bağla (dosyalar kopyalanır; buradan silmek sürücüyü etkilemez)
      if (d.documents.length > 0 && !attachedDriverIds.has(d.id)) {
        setAttachedDriverIds((p) => new Set(p).add(d.id));
        attachMut.mutate({ id: s.id, driverId: d.id }, {
          onSuccess: (res) => { setDocs(res.documents); qc.invalidateQueries(); },
          onError: () => setAttachedDriverIds((p) => { const n = new Set(p); n.delete(d.id); return n; }), // başarısızsa tekrar denenebilsin
        });
      }
    }
  }
  function openEditDriver(driverId: string) {
    setEditDriver(driverId); // sürücü-cari id → CompanyFormModal düzenleme
  }
  useEffect(() => {
    setPlannedDate(s.plannedDate ? new Date(s.plannedDate).toISOString().slice(0, 10) : "");
    setCarrier(s.carrier ?? ""); setVehiclePlate(s.vehiclePlate ?? ""); setTrailerPlate(s.trailerPlate ?? ""); setDriverName(s.driverName ?? "");
    setDriverPhone(s.driverPhone ?? ""); setDriverTc(s.driverTc ?? ""); setWaybillNo(s.waybillNo ?? ""); setDocs(s.documents ?? []);
    setPlannedTime(s.plannedTime ?? "");
    setPalletCount(s.palletCount != null ? String(s.palletCount) : "");
    setLayerCount(s.layerCount != null ? String(s.layerCount) : "");
    setFullWeight(s.fullWeightKg != null ? toTrInput(s.fullWeightKg) : "");
    setEmptyWeight(s.emptyWeightKg != null ? toTrInput(s.emptyWeightKg) : "");
    setWeightErr(null);
    setVehicleClean(s.vehicleCleanOk == null ? "" : s.vehicleCleanOk ? "yes" : "no");
    setFreightParty((s.freightParty as "firma" | "karsi" | null) ?? "");
    setFreightAmount(s.freightAmount != null ? toTrInput(s.freightAmount) : "");
    setFreightCurrency((s.freightCurrency as "TRY" | "USD" | "EUR" | "GBP" | null) ?? "TRY");
    setAmountErr(null);
    setCarrierPick(null); setDriverPick(null); setAttachedDriverIds(new Set());
    setSaved(false);
    updateMut.reset(); uploadMut.reset(); /* eslint-disable-next-line */
  }, [s.id, s.status]);

  /**
   * KAYDEDİLMEMİŞ PLAN İÇERİĞİ — formda yazılmış ama Kaydet'e basılmamış (ya da yüklemesi/bağlaması
   * süren) bilgi. Üst bileşen "Boş Planı Sil" kararında kullanır: sunucu bu girdiyi GÖRMEZ; plan kayıtta
   * boş olsa da kullanıcı ekranda şoför/plaka görüyorsa tek tıkla silinmemeli (yazdığı da giderdi).
   * YALNIZ temkinli yöne çalışır — onaysız silmeyi AÇMAZ, yalnız onay ister → sunucudaki tek tanımla
   * (BOS_PLAN_KOSULU) ayna bozulmaz. Karşılaştırma `save()`in GÖNDERECEĞİ normalize değerle (kırpma ·
   * floor · TR sayı) kayıtlı değer arasında: kaydedilmiş formu yanlışlıkla "kirli" saymasın.
   * Planlanan TARİH sayılmaz: sunucu kuralında da tarih tek başına içerik değildir (BOS_PLAN_SAYILMAZ).
   * Evrak listesi sayılmaz: yükleme/bağlama anında sunucuya yazılır — yalnız SÜREN işlem sayılır.
   */
  const kaydedilmemis: string[] = [];
  if (editable) {
    const metinFarkli = (v: string, kayitli: string | null | undefined) => v.trim() !== (kayitli ?? "").trim();
    const tamsayi = (v: string) => (v.trim() ? Math.max(0, Math.floor(Number(v))) : null);
    const ondalik = (v: string) => (v.trim() ? parseTrNumber(v) : null);
    // NaN (geçersiz giriş) hiçbir şeye eşit değildir → "kirli" sayılır (temkinli yön).
    const sayiFarkli = (a: number | null, b: number | null | undefined) =>
      !(a === (b ?? null) || (a != null && b != null && Math.abs(a - b) < 1e-9));
    if (metinFarkli(carrier, s.carrier)) kaydedilmemis.push("Taşıyıcı Firma");
    if (metinFarkli(vehiclePlate, s.vehiclePlate)) kaydedilmemis.push("Araç Plakası");
    if (metinFarkli(trailerPlate, s.trailerPlate)) kaydedilmemis.push("Dorse Plakası");
    if ((vehicleClean === "" ? null : vehicleClean === "yes") !== (s.vehicleCleanOk ?? null)) kaydedilmemis.push("İç Temizlik");
    if (metinFarkli(driverName, s.driverName)) kaydedilmemis.push("Sürücü Adı");
    if (metinFarkli(driverTc, s.driverTc)) kaydedilmemis.push("Sürücü TC No");
    if (metinFarkli(driverPhone, s.driverPhone)) kaydedilmemis.push("Sürücü Telefonu");
    if (plannedTime !== (s.plannedTime ?? "")) kaydedilmemis.push("Planlanan Saat");
    if (sayiFarkli(tamsayi(palletCount), s.palletCount)) kaydedilmemis.push("Palet Sayısı");
    if (sayiFarkli(tamsayi(layerCount), s.layerCount)) kaydedilmemis.push("Kat Sayısı");
    if (sayiFarkli(ondalik(fullWeight), s.fullWeightKg)) kaydedilmemis.push("Dolu Kantar");
    if (sayiFarkli(ondalik(emptyWeight), s.emptyWeightKg)) kaydedilmemis.push("Boş Kantar");
    if ((freightParty || null) !== (s.freightParty ?? null)) kaydedilmemis.push("Nakliye Kime Ait");
    if (freightParty === "firma") {
      if (sayiFarkli(ondalik(freightAmount), s.freightAmount)) kaydedilmemis.push("Nakliye Tutarı");
      if (freightCurrency !== (s.freightCurrency ?? "TRY")) kaydedilmemis.push("Para Birimi");
    }
    if (metinFarkli(waybillNo, s.waybillNo)) kaydedilmemis.push("İrsaliye No");
    if (updateMut.isPending) kaydedilmemis.push("kaydediliyor");
    if (uploadMut.isPending) kaydedilmemis.push("evrak yükleniyor");
    if (attachMut.isPending) kaydedilmemis.push("sürücü evrakı bağlanıyor");
  }
  const kaydedilmemisOzet = kaydedilmemis.join(", ");
  useEffect(() => {
    onKaydedilmemis?.(kaydedilmemisOzet);
    return () => onKaydedilmemis?.(""); // kart kalkınca (çekmece kapandı) üstte bayat "kirli" kalmasın
  }, [kaydedilmemisOzet, onKaydedilmemis]);

  function save() {
    setAmountErr(null);
    setWeightErr(null);
    // Nakliye tutarı: TR biçimi (12.500 → 12500 · 1.234,56 → 1234.56); geçersizse SESSİZCE null'a düşme — kaydı durdur.
    let amountNum: number | null = null;
    if (freightParty === "firma" && freightAmount.trim()) {
      amountNum = parseTrNumber(freightAmount);
      if (!Number.isFinite(amountNum) || amountNum < 0) {
        setAmountErr("Geçersiz tutar. Örn: 12.500 veya 1.234,56");
        return;
      }
    }
    // Kantar: TR biçimli sayı; boş > dolu olamaz (net negatif çıkmasın).
    let fullNum: number | null = null;
    let emptyNum: number | null = null;
    if (fullWeight.trim()) {
      fullNum = parseTrNumber(fullWeight);
      if (!Number.isFinite(fullNum) || fullNum < 0) return setWeightErr("Geçersiz dolu kantar değeri. Örn: 24.360 veya 24.360,5");
    }
    if (emptyWeight.trim()) {
      emptyNum = parseTrNumber(emptyWeight);
      if (!Number.isFinite(emptyNum) || emptyNum < 0) return setWeightErr("Geçersiz boş kantar değeri. Örn: 14.200");
    }
    if (fullNum != null && emptyNum != null && emptyNum > fullNum) {
      return setWeightErr("Boş kantar ağırlığı dolu ağırlıktan büyük olamaz.");
    }
    updateMut.mutate({
      id: s.id,
      plannedDate: plannedDate ? new Date(plannedDate + "T00:00:00").toISOString() : null,
      carrier: carrier.trim() || null, vehiclePlate: vehiclePlate.trim() || null, trailerPlate: trailerPlate.trim() || null,
      driverName: driverName.trim() || null, driverPhone: driverPhone.trim() || null, driverTc: driverTc.trim() || null, waybillNo: waybillNo.trim() || null,
      plannedTime: plannedTime || null,
      palletCount: palletCount.trim() ? Math.max(0, Math.floor(Number(palletCount))) : null,
      layerCount: layerCount.trim() ? Math.max(0, Math.floor(Number(layerCount))) : null,
      vehicleCleanOk: vehicleClean === "" ? null : vehicleClean === "yes",
      freightParty: freightParty || null,
      // Karşıya aitse tutar gönderilmez (backend de temizler)
      freightAmount: freightParty === "firma" ? amountNum : null,
      freightCurrency: freightParty === "firma" ? freightCurrency : null,
      fullWeightKg: fullNum,
      emptyWeightKg: emptyNum,
    }, {
      onSuccess: () => {
        qc.invalidateQueries();
        setSaved(true);
        if (savedTimer.current) window.clearTimeout(savedTimer.current);
        savedTimer.current = window.setTimeout(() => setSaved(false), 3500);
      },
    });
  }
  /**
   * EVRAK YÜKLEME — tek noktadan: dosya seçici, SÜRÜKLE-BIRAK ve YAPIŞTIR (Ctrl+V) aynı yolu kullanır.
   * Dosyalar SIRAYLA yüklenir: aynı anda paralel `mutate` çağırmak `docs` state'inde son-yazan-kazanır
   * yarışı yaratır ve önceki yüklemeyi listeden düşürürdü.
   */
  async function evrakYukle(files: File[]) {
    if (files.length === 0) return;
    setDropHata(null);
    for (const file of files) {
      try {
        const dataBase64 = await new Promise<string>((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result as string);
          r.onerror = () => rej(new Error(`${file.name} okunamadı.`));
          r.readAsDataURL(file);
        });
        // Panoya yapıştırılan görselin adı boş olabilir → uzantılı bir ad üretilir,
        // yoksa sunucudaki uzantı beyaz listesi dosyayı reddeder.
        const ad = file.name && file.name.includes(".")
          ? file.name
          : `yapistirilan-${Date.now()}.${(file.type.split("/")[1] || "png").replace("jpeg", "jpg")}`;
        const res = await uploadMut.mutateAsync({ id: s.id, filename: ad, dataBase64 });
        setDocs((p) => [...p, res.url]);
      } catch (err) {
        setDropHata(err instanceof Error ? err.message : String(err));
        break; // ilk hatada dur — kalanları sessizce atlamak yanıltıcı olurdu
      }
    }
    qc.invalidateQueries();
  }
  function onPickDoc(e: React.ChangeEvent<HTMLInputElement>) {
    const files = [...(e.target.files ?? [])]; e.target.value = "";
    void evrakYukle(files);
  }
  /**
   * SÜRÜKLE-BIRAK. WhatsApp NOTU: WhatsApp Web'den doğrudan sürüklenen medya gerçek bir dosya
   * DEĞİL, `blob:web.whatsapp.com/...` adresidir — tarayıcı güvenliği o adresi başka bir siteden
   * okutmaz, dolayısıyla yüklenemez. Bu durumda SESSİZ KALMAK yerine kullanıcıya ne yapacağı
   * söylenir: görsele sağ tık → Kopyala → buraya Ctrl+V, ya da önce indirip dosyayı sürükle.
   */
  function onDropDoc(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const files = [...(e.dataTransfer.files ?? [])];
    if (files.length > 0) { void evrakYukle(files); return; }
    const uri = e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain");
    setDropHata(
      uri.startsWith("blob:") || uri.startsWith("http")
        ? "Bu öğe dosya olarak sürüklenemiyor (WhatsApp Web/tarayıcı içi görsel). Görsele sağ tıklayıp Kopyala deyin ve buraya Ctrl+V ile yapıştırın; ya da önce cihazınıza indirip dosyayı sürükleyin."
        : "Sürüklenen öğede yüklenebilir dosya bulunamadı.",
    );
  }
  /** Ctrl+V ile pano yapıştırma — WhatsApp görselleri için EN GÜVENİLİR yol. */
  function onPasteDoc(e: React.ClipboardEvent) {
    const files = [...(e.clipboardData?.files ?? [])];
    if (files.length === 0) return;
    e.preventDefault();
    void evrakYukle(files);
  }
  function removeDoc(url: string) {
    removeMut.mutate({ id: s.id, url }, { onSuccess: () => { setDocs((p) => p.filter((u) => u !== url)); qc.invalidateQueries(); } });
  }

  const ro = (v: string) => <div style={{ padding: "8px 10px", background: "var(--neutral-50)", borderRadius: "var(--radius-md)", fontSize: 13, minHeight: 20 }}>{v || <span style={{ color: "var(--neutral-400)" }}>—</span>}</div>;
  // req: zorunlu alan → etikette kırmızı yıldız (yalnız taslakta; salt-okunurda anlamsız).
  const fld = (label: string, node: React.ReactNode, req?: boolean) => (
    <div className="form-field">
      <label className="lbl">{label}{req && showReq ? <span className="req"> *</span> : null}</label>
      {node}
    </div>
  );
  // Grup kartı: alanları türe göre kümeleyen ikonlu başlık şeritli iç kart (.fgroup).
  // stepKey: bu grup hangi wizard adımına ait — aktif değilse GİZLENİR (unmount edilmez, state korunur).
  const grp = (stepKey: string, icon: string, title: string, children: React.ReactNode, opts?: { hint?: string; foot?: React.ReactNode }) => (
    <section className={`fgroup${step === stepKey ? "" : " shp-hide"}`}>
      <div className="fgroup-head"><span className="ic"><Icon name={icon} size={13} /></span><span className="t">{title}</span>{opts?.hint ? <span className="s">{opts.hint}</span> : null}</div>
      <div className="fgroup-body">{children}</div>
      {opts?.foot ?? null}
    </section>
  );

  // Net çıkış kg (canlı): dolu − boş; ikisi de geçerliyse gösterilir.
  const fwNum = fullWeight.trim() ? parseTrNumber(fullWeight) : NaN;
  const ewNum = emptyWeight.trim() ? parseTrNumber(emptyWeight) : NaN;
  const netKg = Number.isFinite(fwNum) && Number.isFinite(ewNum) ? fwNum - ewNum : null;
  // Salt-okunurda net, sunucu değerinden hesaplanır (state string'ine bağımlı değil).
  const netShown = editable ? netKg : s.fullWeightKg != null && s.emptyWeightKg != null ? s.fullWeightKg - s.emptyWeightKg : null;

  return (
    <>
    {/* "lines" adımında (Yükleme Listesi) form kartı gizlenir — ama MOUNTED kalır: kaydedilmemiş girdi uçmasın. */}
    <div className={`d-card shp-wiz-card${step === "lines" ? " shp-hide" : ""}`}>
      <div className="d-card-head">
        <div className="left"><span className="t">Sevkiyat Planı / Evrak</span>{!editable ? <span className="s">salt-okunur (sevk edildi)</span> : null}</div>
        {editable ? (
          <button type="button" className="btn-md primary" style={{ height: 30 }} disabled={updateMut.isPending} onClick={save}>
            {updateMut.isPending ? "Kaydediliyor…" : saved ? <><Icon name="check" size={14} />Kaydedildi</> : "Kaydet"}
          </button>
        ) : null}
      </div>
      <div className="d-card-body">
        {saved ? (
          <div className="form-banner-ok" role="status"><Icon name="checkCircle" size={14} />Sevkiyat planı kaydedildi.</div>
        ) : null}
        {updateMut.error ? <div className="form-banner-err">{updateMut.error.message}</div> : null}
        {attachMut.error ? <div className="form-banner-err" role="alert">Sürücü evrakları sevkiyata eklenemedi: {attachMut.error.message}</div> : null}
        <div className="fgroup-grid">
          {/* ── Taşıyıcı & Araç ── */}
          {grp("carrier", "truck", "Taşıyıcı & Araç", <>
            {editable ? (
              <div className="form-field">
                <label className="lbl">Kayıtlı Taşıyıcı <span className="help" style={{ fontWeight: 400 }}>(seçince aşağıyı doldurur)</span></label>
                <div style={{ display: "flex", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <SearchSelect value={carrierPick} options={carrierOpts} onChange={pickCarrier} icon="truck" placeholder="Taşıyıcı seç…" searchPlaceholder="Firma ara…" emptyText="Kayıtlı taşıyıcı yok" />
                  </div>
                  <button type="button" className="btn-md secondary" style={{ whiteSpace: "nowrap", height: "var(--field-h)" }} onClick={() => setAddCarrierOpen(true)}><Icon name="plus" size={14} />Ekle</button>
                </div>
              </div>
            ) : null}
            {fld("Taşıyıcı Firma", editable ? <input className="form-input" value={carrier} onChange={(e) => setCarrier(e.target.value)} maxLength={200} placeholder="Nakliyeci firma" /> : ro(carrier))}
            {/* Çekici + dorse yan yana: dorse OPSİYONEL (tır değilse boş bırakılır). */}
            <div className="form-row" style={{ gap: 12 }}>
              {fld("Araç Plakası", editable ? <input className="form-input" value={vehiclePlate} onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())} maxLength={30} placeholder="34 ABC 123" /> : ro(vehiclePlate), true)}
              {fld("Dorse Plakası", editable ? <input className="form-input" value={trailerPlate} onChange={(e) => setTrailerPlate(e.target.value.toUpperCase())} maxLength={30} placeholder="34 DR 456 (varsa)" /> : ro(trailerPlate))}
            </div>
            <div className="form-row" style={{ gap: 12 }}>
              {fld("İç Temizlik Sevkiyata Uygun", editable ? (
                <select className="form-select" value={vehicleClean} onChange={(e) => setVehicleClean(e.target.value as "" | "yes" | "no")}>
                  <option value="">— seçilmedi —</option>
                  <option value="yes">Evet</option>
                  <option value="no">Hayır</option>
                </select>
              ) : ro(vehicleClean === "" ? "" : vehicleClean === "yes" ? "Evet" : "Hayır"))}
            </div>
          </>)}
          {/* ── Sürücü ── */}
          {grp("driver", "users", "Sürücü", <>
            {editable ? (
              <div className="form-field">
                <label className="lbl">Kayıtlı Sürücü <span className="help" style={{ fontWeight: 400 }}>(seçince aşağıyı doldurur)</span></label>
                <div style={{ display: "flex", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <SearchSelect value={driverPick} options={driverOpts} onChange={pickDriver} icon="users" placeholder="Sürücü seç…" searchPlaceholder="Ad / TC ara…" emptyText="Kayıtlı sürücü yok"
                      itemAction={{ icon: "edit", label: "Sürücüyü düzenle", onAction: openEditDriver }} />
                  </div>
                  <button type="button" className="btn-md secondary" style={{ whiteSpace: "nowrap", height: "var(--field-h)" }} onClick={() => setAddDriverOpen(true)}><Icon name="plus" size={14} />Ekle</button>
                </div>
              </div>
            ) : null}
            {fld("Sürücü Adı", editable ? <input className="form-input" value={driverName} onChange={(e) => setDriverName(e.target.value)} maxLength={120} /> : ro(driverName), true)}
            <div className="form-row" style={{ gap: 12 }}>
              {fld("Sürücü TC No", editable ? <input className="form-input" value={driverTc} onChange={(e) => setDriverTc(e.target.value.replace(/\D/g, "").slice(0, 11))} maxLength={11} inputMode="numeric" placeholder="11 haneli" /> : ro(driverTc))}
              {fld("Sürücü Telefonu", editable ? <input className="form-input" value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} maxLength={40} inputMode="tel" /> : ro(driverPhone))}
            </div>
          </>)}
          {/* ── Sevk & Yükleme Planı ── */}
          {grp("plan", "clock", "Sevk & Yükleme Planı", <>
            <div className="form-row" style={{ gap: 12 }}>
              {fld("Planlanan Sevk Tarihi", editable ? <input className="form-input" type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} /> : ro(fmtDateOnly(s.plannedDate)), true)}
              {fld("Planlanan Saat", editable ? <input className="form-input" type="time" value={plannedTime} onChange={(e) => setPlannedTime(e.target.value)} /> : ro(plannedTime), true)}
            </div>
            <div className="form-row" style={{ gap: 12 }}>
              {fld("Palet Sayısı", editable ? <input className="form-input" type="number" min={0} step={1} value={palletCount} onChange={(e) => setPalletCount(e.target.value)} placeholder="toplam" inputMode="numeric" /> : ro(palletCount))}
              {fld("Kat Sayısı", editable ? <input className="form-input" type="number" min={0} step={1} value={layerCount} onChange={(e) => setLayerCount(e.target.value)} placeholder="toplam" inputMode="numeric" /> : ro(layerCount))}
            </div>
          </>)}
          {/* ── Kantar / Tartım ── */}
          {grp("weigh", "weight", "Kantar / Tartım", <>
            <div className="form-row" style={{ gap: 12 }}>
              {fld("Dolu Kantar (kg)", editable ? (
                <>
                  <input className={`form-input${weightErr ? " err" : ""}`} type="text" value={fullWeight} onChange={(e) => { setFullWeight(e.target.value.replace(/[^\d.,]/g, "")); setWeightErr(null); }} placeholder="örn. 24.360" inputMode="decimal" aria-invalid={!!weightErr} />
                  {weightErr ? <span className="err" role="alert">{weightErr}</span> : null}
                </>
              ) : ro(s.fullWeightKg != null ? `${fmtQty(s.fullWeightKg)} kg` : ""), true)}
              {fld("Boş Kantar (kg)", editable ? (
                <input className="form-input" type="text" value={emptyWeight} onChange={(e) => { setEmptyWeight(e.target.value.replace(/[^\d.,]/g, "")); setWeightErr(null); }} placeholder="örn. 14.200" inputMode="decimal" />
              ) : ro(s.emptyWeightKg != null ? `${fmtQty(s.emptyWeightKg)} kg` : ""), true)}
            </div>
          </>, {
            hint: "net = dolu − boş",
            foot: (
              <div className="fgroup-foot">
                <span>Net Çıkış</span>
                <b className="mono" style={{ fontSize: 13, color: netShown == null ? "var(--neutral-400)" : netShown < 0 ? "var(--danger)" : "var(--neutral-900)" }}>
                  {netShown == null ? "—" : `${fmtQty(netShown)} kg`}
                </b>
              </div>
            ),
          })}
          {/* ── Nakliye ── */}
          {grp("freight", "banknote", "Nakliye", <>
            {fld("Nakliye Kime Ait", editable ? (
              <select className="form-select" value={freightParty} onChange={(e) => setFreightParty(e.target.value as "" | "firma" | "karsi")}>
                <option value="">— seçilmedi —</option>
                <option value="firma">Firmaya Aittir</option>
                <option value="karsi">Karşıya Aittir</option>
              </select>
            ) : ro(freightParty === "" ? "" : freightParty === "firma" ? "Firmaya Aittir" : "Karşıya Aittir"), true)}
            {freightParty === "firma" ? fld("Nakliye Tutarı (gider kaydı)", editable ? (
              <>
                <div style={{ display: "flex", gap: 6 }}>
                  <input className={`form-input${amountErr ? " err" : ""}`} style={{ flex: 1 }} type="text" value={freightAmount} onChange={(e) => { setFreightAmount(e.target.value.replace(/[^\d.,]/g, "")); setAmountErr(null); }} placeholder="0,00" inputMode="decimal" aria-invalid={!!amountErr} />
                  <select className="form-select" style={{ width: 84 }} value={freightCurrency} onChange={(e) => setFreightCurrency(e.target.value as "TRY" | "USD" | "EUR" | "GBP")} aria-label="Para birimi">
                    <option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
                  </select>
                </div>
                {amountErr ? <span className="err" role="alert">{amountErr}</span> : null}
              </>
            ) : ro(s.freightAmount != null ? `${fmtMoney(s.freightAmount)} ${freightCurrency}` : "")) : null}
          </>)}
          {/* ── İrsaliye & Evraklar ── */}
          {grp("waybill", "fileText", "İrsaliye & Evraklar", <>
            {fld("İrsaliye No", editable ? <input className="form-input" value={waybillNo} onChange={(e) => setWaybillNo(e.target.value)} maxLength={60} placeholder="Resmi irsaliye no" /> : ro(waybillNo), true)}
            <div className="form-field">
              <label className="lbl">Evraklar <span className="help" style={{ fontWeight: 400 }}>(foto · PDF · Excel/Word · ≤8 MB)</span></label>
              {/* BIRAKMA ALANI: dosyalar buraya sürüklenebilir ya da Ctrl+V ile yapıştırılabilir.
                  `tabIndex` şart — odaklanamayan öğe paste olayı almaz. */}
              <div
                onDragOver={(e) => { if (s.status !== "cancelled") { e.preventDefault(); setDragOver(true); } }}
                onDragLeave={() => setDragOver(false)}
                onDrop={s.status !== "cancelled" ? onDropDoc : undefined}
                onPaste={s.status !== "cancelled" ? onPasteDoc : undefined}
                tabIndex={s.status !== "cancelled" ? 0 : -1}
                aria-label="Evrak bırakma alanı — dosya sürükleyin veya Ctrl+V ile yapıştırın"
                style={{
                  display: "flex", flexWrap: "wrap", gap: 8, padding: 8,
                  borderRadius: "var(--radius-md)",
                  border: `1px dashed ${dragOver ? "var(--primary)" : "transparent"}`,
                  background: dragOver ? "var(--primary-subtle)" : "transparent",
                  transition: "background 150ms, border-color 150ms",
                }}
              >
                {docs.map((url) => <DocChip key={url} url={url} onOpen={setPreviewUrl} onRemove={editable ? () => removeDoc(url) : undefined} busy={removeMut.isPending} />)}
                {s.status !== "cancelled" ? (
                  <label style={{ width: 78, height: 78, borderRadius: "var(--radius-md)", border: "1px dashed var(--neutral-300)", display: "inline-flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: uploadMut.isPending ? "wait" : "pointer", color: "var(--neutral-500)", fontSize: 11, gap: 3, textAlign: "center" }}>
                    <Icon name="upload" size={16} />{uploadMut.isPending ? "…" : "Ekle"}
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.xlsx,.xls,.csv,.docx,.doc,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                      hidden onChange={onPickDoc} disabled={uploadMut.isPending}
                    />
                  </label>
                ) : null}
              </div>
              {s.status !== "cancelled" ? (
                <span className="help" style={{ fontSize: 11, color: "var(--neutral-500)" }}>
                  Dosyaları bu alana sürükleyip bırakabilir ya da <b>Ctrl+V</b> ile yapıştırabilirsiniz (WhatsApp görselinde: sağ tık → Kopyala → Ctrl+V).
                </span>
              ) : null}
              {dropHata ? <span className="err" role="alert">{dropHata}</span> : null}
              {uploadMut.error ? <span className="err" role="alert">{uploadMut.error.message}</span> : null}
            </div>
          </>)}
        </div>
      </div>
    </div>
    <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
    <CompanyFormModal open={addCarrierOpen} companyId={null} defaultRole="carrier" onClose={() => setAddCarrierOpen(false)} onCreated={(c) => { setCarrier(c.name); setCarrierPick(c.id); carrierQ.refetch(); }} />
    <CompanyFormModal open={addDriverOpen || !!editDriver} companyId={editDriver} defaultRole="driver" onClose={() => { setAddDriverOpen(false); setEditDriver(null); driverQ.refetch(); }}
      onSaved={(d) => { if (d.id === driverPick) { setDriverName(d.name); setDriverTc(d.tc ?? ""); setDriverPhone(d.phone ?? ""); } }}
      onCreated={(d) => {
      setDriverName(d.name); setDriverTc(d.tc ?? ""); setDriverPhone(d.phone ?? ""); setDriverPick(d.id); driverQ.refetch();
      // Yeni sürücüyle yüklenen evrakları da bu sevkiyata bağla (sunucu idempotent; evrak yoksa no-op)
      setAttachedDriverIds((p) => new Set(p).add(d.id));
      attachMut.mutate({ id: s.id, driverId: d.id }, {
        onSuccess: (res) => { if (res.addedCount > 0) { setDocs(res.documents); } qc.invalidateQueries(); },
        onError: () => setAttachedDriverIds((p) => { const n = new Set(p); n.delete(d.id); return n; }),
      });
    }} />
    </>
  );
}

/**
 * WIZARD ADIMLARI — sol raydaki sıra. `req(s)` = o adımda KAYDEDİLMİŞ (sunucudaki) zorunlu alanlardan boş
 * olanların etiketleri. Doğrulama bilinçli olarak SUNUCU verisinden yapılır: "Onayla" ancak veri gerçekten
 * kaydedilmişse açılır; kullanıcı yazıp Kaydet'e basmadıysa ⚠ durur ve bu doğru uyarıdır.
 */
const WIZ_STEPS: Array<{ key: string; label: string; icon: string; req: (s: ShipmentData) => string[] }> = [
  { key: "carrier", label: "Taşıyıcı & Araç", icon: "truck", req: (s) => (s.vehiclePlate?.trim() ? [] : ["Araç Plakası"]) },
  { key: "driver", label: "Sürücü", icon: "users", req: (s) => (s.driverName?.trim() ? [] : ["Sürücü Adı"]) },
  { key: "plan", label: "Sevk Planı", icon: "clock", req: (s) => [...(s.plannedDate ? [] : ["Planlanan Sevk Tarihi"]), ...(s.plannedTime ? [] : ["Planlanan Saat"])] },
  { key: "weigh", label: "Kantar", icon: "weight", req: (s) => [...(s.fullWeightKg != null ? [] : ["Dolu Kantar"]), ...(s.emptyWeightKg != null ? [] : ["Boş Kantar"])] },
  { key: "freight", label: "Nakliye", icon: "banknote", req: (s) => (s.freightParty ? [] : ["Nakliye Kime Ait"]) },
  { key: "waybill", label: "İrsaliye & Evraklar", icon: "fileText", req: (s) => (s.waybillNo?.trim() ? [] : ["İrsaliye No"]) },
  { key: "lines", label: "Yükleme Listesi", icon: "boxes", req: () => [] },
];

export function ShipmentDetailDrawer({ open, shipmentId, onClose, onDeleted }: ShipmentDetailProps) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<string>(WIZ_STEPS[0]!.key);
  const [confirm, setConfirm] = useState<null | { title: string; message: React.ReactNode; tone: "danger" | "primary"; confirmLabel: string; run: () => void }>(null);
  const s = useShipmentData(shipmentId, open);
  // Adım başına eksik zorunlu alanlar (yalnız taslakta anlamlı; onaylı sevkiyatta uyarı gösterilmez).
  const showReq = s?.status === "draft";
  const stepMissing = useMemo<Record<string, string[]>>(() => {
    const out: Record<string, string[]> = {};
    for (const st of WIZ_STEPS) out[st.key] = s && showReq ? st.req(s) : [];
    return out;
  }, [s, showReq]);
  const missingAll = useMemo(() => WIZ_STEPS.flatMap((st) => stepMissing[st.key] ?? []), [stepMissing]);
  const stepIdx = Math.max(0, WIZ_STEPS.findIndex((st) => st.key === step));
  // Başka bir sevkiyat açılınca ilk adıma dön. Sevk edilmiş kayıtta da ilk adım: salt-okunur evrak/irsaliye
  // bilgisi (irsaliye no, yüklenen evraklar) doğrudan görünsün — "lines"a zorlanırsa form kartı gizli kalırdı.
  useEffect(() => {
    if (!open || !s) return;
    setStep(WIZ_STEPS[0]!.key);
  }, [open, s?.id, s?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  // Yükleme Formu başlığı: sevkiyat satırının lokasyonundan (→ tesis) firma künyesi.
  const shipLocId = s?.lines[0]?.locationId ?? null;
  const profQ = trpc.settings.companyProfile.getForLocation.useQuery({ locationId: shipLocId ?? "" }, { enabled: open && !!shipLocId });
  const confirmMut = trpc.shipment.confirm.useMutation();
  const cancelMut = trpc.shipment.cancel.useMutation();
  // İptal gerekçesi ZORUNLU (sunucu da istiyor): canlıda planlar saniyeler içinde
  // gerekçesiz iptal ediliyor ve sipariş "hiç sevk edilmemiş" gibi kalıyordu.
  const cancelReasonRef = useRef<HTMLTextAreaElement>(null);
  const [cancelErr, setCancelErr] = useState<string | null>(null);
  /**
   * Sunucunun GÖRMEDİĞİ girdi: formda kaydedilmemiş şoför/plaka… (ShipmentEvrakCard) ve işaretlenip
   * eklenmemiş lotlar (ManualLineBuilder). "" = temiz. Plan kayıtta boş (`s.bosPlan`) olsa da bunlardan
   * biri varsa silme ONAY ister — kullanıcı ekranda gördüğü bilgiyi tek tıkla kaybetmesin.
   */
  const [evrakKirli, setEvrakKirli] = useState("");
  const [lotSecimKirli, setLotSecimKirli] = useState("");
  const kaydedilmemis = [evrakKirli, lotSecimKirli].filter(Boolean).join(" · ");
  const onaysizSilinebilir = !!s?.bosPlan && !kaydedilmemis;
  const removeLineMut = trpc.shipment.removeLine.useMutation();
  const updateLineMut = trpc.shipment.updateLine.useMutation();
  // Sevke GİRECEK satır sayısı — "yüklenmeyecek" işaretliler listede kalır ama sayılmaz.
  const effLineCount = s ? s.lines.filter((l) => !l.excluded).length : 0;
  const setAllMut = trpc.shipment.setAllLinesExcluded.useMutation();
  // Hızlı lot arama: 59 satırlık pick list'te aranan lotu anında bulup anahtarını çevirmek için.
  const [lineQ, setLineQ] = useState("");
  const lq = lineQ.trim().toLocaleLowerCase("tr");
  const visibleLines = s
    ? (lq ? s.lines.filter((l) => `${l.lotCode} ${l.itemCode} ${l.itemName} ${l.locationCode}`.toLocaleLowerCase("tr").includes(lq)) : s.lines)
    : [];
  // Başlıktaki "tümü" switch'i GÖRÜNEN satırlara uygulanır (arama açıkken 3 satır görüp 59'u değiştirmeyelim).
  const visLoaded = visibleLines.filter((l) => !l.excluded).length;
  const allLoaded = visibleLines.length > 0 && visLoaded === visibleLines.length;
  // Lot ikamesi: hangi satırın seçici açık (lineId) — satır-içi popover.
  const [subLineId, setSubLineId] = useState<string | null>(null);
  const [preflighting, setPreflighting] = useState(false);
  const [opErr, setOpErr] = useState<string | null>(null); // satır düzenleme hatası (kullanıcıya görünür)
  const utils = trpc.useUtils();

  /**
   * ═══ LOT SAĞLIĞI LİSTEDE GÖRÜNSÜN ═══
   *
   * Ön kontrol (preflight) EskİDEN yalnız `startConfirm()` içinde çalışıyordu. Sonuç: planda
   * ölü bir lot varsa (ör. üretim partisi silinmiş → lot `consumed`) satır listede SAPASAĞLAM
   * görünüyor, kullanıcı Yükleme anahtarını açabiliyor ve durumu ancak ONAY anında
   * "lot durumu 'consumed'" hatasıyla öğreniyordu (canlıda SVK-2026-00064 / LOT-2026-09402).
   *
   * Artık çekmece açılınca da çalışıyor ve sorunlar SATIR ÜZERİNDE gösteriliyor.
   * Kural UI'da YENİDEN TÜRETİLMİYOR: tek kaynak `reconcileShipmentLines` (sunucu) —
   * aksi halde liste ile onay iki ayrı doğruya inanır.
   */
  const pfQ = trpc.shipment.preflight.useQuery(
    { shipmentId: shipmentId ?? "" },
    { enabled: open && !!shipmentId && s?.status === "draft", staleTime: 0, refetchOnWindowFocus: false },
  );
  /**
   * `autoFixed` AYIRICIDIR — "sorun" ≠ "engel":
   *  · `autoFixed: true`  → uzlaştırma kendi çözüyor (lot taşınmış / bakiye yetersiz-kısmi).
   *                         Onay GEÇER, yalnız bilgilendirilir. "Yüklenemez" demek YANLIŞ olurdu.
   *  · `autoFixed: false` → onayı DURDURUR (ölü lot · SKT · yabancı earmark · bakiye yok · çok depo).
   * Sunucuda her `autoFixed:false` dalı aynı anda `blocking`e de yazar → ikisi birebir örtüşür.
   */
  const issueByLine = useMemo(() => {
    const m = new Map<string, { kind: string; detail: string; lotCode: string; blocking: boolean }>();
    for (const i of pfQ.data?.issues ?? []) {
      if (!i.lineId) continue;
      const prev = m.get(i.lineId);
      // Aynı satırda birden çok kayıt olursa ENGEL olan kazanır (bilgi notu onu gizlemesin).
      if (prev?.blocking && i.autoFixed) continue;
      m.set(i.lineId, { kind: i.kind, detail: i.detail, lotCode: i.lotCode, blocking: !i.autoFixed });
    }
    return m;
  }, [pfQ.data]);
  /** Onayı fiilen durduran satırlar — hem satır işaretlemesi hem üstteki şerit bunu kullanır. */
  const blockedLineIds = useMemo(
    () => new Set([...issueByLine].filter(([, v]) => v.blocking).map(([k]) => k)),
    [issueByLine],
  );

  /** ONAY: önce lotları kontrol et (taşınmış/eksik/karantina), sonucu kullanıcıya göster, sonra onayla. */
  async function startConfirm() {
    if (!s) return;
    setPreflighting(true);
    try {
      const pf = await utils.shipment.preflight.fetch({ shipmentId: s.id }, { staleTime: 0 });
      const auto = pf.issues.filter((i) => i.autoFixed);
      const manual = pf.issues.filter((i) => !i.autoFixed);
      if (pf.blocking.length > 0) {
        // Çözülemeyen fark: onaya izin verme, ne yapılacağını söyle.
        setConfirm({
          title: "Lotlar sevke uygun değil",
          message: (
            <>
              <p style={{ marginTop: 0 }}>Aşağıdaki satırlar bu haliyle sevk edilemez. İlgili satırın <b>Yükleme</b> anahtarını kapatın ya da lotunu değiştirin:</p>
              <ul className="shp-pf-list">{pf.blocking.map((b, i) => <li key={i}>{b}</li>)}</ul>
            </>
          ),
          tone: "danger", confirmLabel: "Anladım", run: async () => {},
        });
        return;
      }
      setConfirm({
        title: "Sevkiyatı onayla?",
        message: (
          <>
            <p style={{ marginTop: 0 }}><b>{s.code}</b> · <b>{effLineCount}</b> satır için <b>stok çıkışı</b> yapılacak. Bu işlem geri alınamaz.</p>
            {auto.length > 0 ? (
              <>
                <p className="shp-pf-head">Yükleme öncesi kontrolde şu farklar bulundu ve <b>otomatik düzeltilecek</b>:</p>
                <ul className="shp-pf-list">{auto.map((i) => <li key={i.lineId}><span className="mono">{i.lotCode}</span> — {i.detail}</li>)}</ul>
              </>
            ) : null}
            {manual.length > 0 ? (
              <ul className="shp-pf-list">{manual.map((i) => <li key={i.lineId}><span className="mono">{i.lotCode}</span> — {i.detail}</li>)}</ul>
            ) : null}
            {auto.length === 0 && manual.length === 0 ? <p className="shp-pf-head">Lot kontrolü temiz — fark yok.</p> : null}
          </>
        ),
        tone: "primary",
        confirmLabel: "Onayla, stok çıkışı yap",
        run: () => run(() => confirmMut.mutateAsync({ id: s.id })),
      });
    } catch (e) {
      setConfirm({ title: "Kontrol başarısız", message: <>{(e as Error).message}</>, tone: "danger", confirmLabel: "Kapat", run: async () => {} });
    } finally {
      setPreflighting(false);
    }
  }

  const sb = s ? STATUS_BADGE[s.status] : null;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setOpErr(null);
    try { await fn(); await qc.invalidateQueries(); }
    catch (e) {
      // Sunucu reddi (fazla sevkiyat tavanı, atanamaz lot…) SESSİZ kalmasın: ekranda göster ve
      // veriyi tazele ki alan sunucudaki gerçek değere dönsün (uncontrolled input reddedileni tutmasın).
      setOpErr((e as Error).message);
      await qc.invalidateQueries();
    }
    finally { setBusy(false); }
  }

  /** "İptal Et ve Sil" — DOLU plan: gerekçe penceresi (bugünkü akış). `not`: onaysız silme reddedildiyse nedeni. */
  function iptalPenceresiAc(not?: string) {
    if (!s) return;
    setCancelErr(null);
    setConfirm({
      title: "Sevkiyat planını sil?",
      tone: "danger",
      confirmLabel: "İptal Et ve Sil",
      message: (
        <>
          {not ? <div className="form-banner-warn" role="status"><Icon name="alertTriangle" size={14} />{not}</div> : null}
          {/* İPTAL ARTIK KALICI SİLME — metin bunu SÖYLEMELİ. Eskiden plan "iptal"
              durumunda listede kalıyordu; şimdi kayıt tamamen gidiyor, geri alınamaz. */}
          <div><b>{s.code}</b> planı <b>kalıcı olarak silinecek</b> — geri alınamaz.
          Stok düşmez, sipariş <b>yeniden planlanabilir</b> hâle döner.</div>
          <div style={{ marginTop: 8, fontSize: 12.5, color: "var(--neutral-600)" }}>
            Plan artık sevkiyat listesinde ve sipariş geçmişinde görünmeyecek; gerekçe yalnız denetim kaydında kalır.
          </div>
          <div className="form-field" style={{ marginTop: 12, marginBottom: 0 }}>
            <label className="lbl" htmlFor="svk-cancel-reason">Gerekçe <span className="req">*</span></label>
            <textarea id="svk-cancel-reason" ref={cancelReasonRef} className="form-textarea" rows={2} maxLength={300}
              placeholder="Örn. Araç gelmedi · Yanlış sipariş seçildi · Müşteri erteledi" />
          </div>
        </>
      ),
      run: () => {
        const g = (cancelReasonRef.current?.value ?? "").trim();
        if (g.length < 3) { setCancelErr("İptal gerekçesi zorunlu (en az 3 karakter)."); return; }
        // Kayıt SİLİNDİĞİ için çekmece açık kalamaz: `shipment.get` 404 döner ve
        // kullanıcı "Sevkiyat bulunamadı" hatasıyla baş başa kalırdı. Başarıda kapat.
        const code = s.code;
        void run(async () => { await cancelMut.mutateAsync({ id: s.id, reason: g }); onClose(); onDeleted?.(`${code} sevkiyat planı silindi.`); });
      },
    });
  }

  /**
   * BOŞ PLAN — gerekçe penceresi AÇILMADAN silinir. `s.bosPlan` sunucunun silme ucuyla AYNI
   * koşulundan (BOS_PLAN_KOSULU) gelir; karar yine sunucuda, DELETE'in kendi WHERE'inde verilir.
   * Çekmece açıkken satır/evrak eklendiyse ve `shipment.get` henüz tazelenmediyse sunucu
   * PRECONDITION_FAILED döner → bugünkü gerekçe penceresi nedeniyle açılır (sessiz hata yok).
   */
  const bosSilRef = useRef(false); // çift tık iki istek atmasın (busy ancak bir sonraki render'da görünür)
  async function bosPlaniSil() {
    if (!s || bosSilRef.current) return;
    bosSilRef.current = true;
    setBusy(true);
    setCancelErr(null);
    cancelMut.reset();
    const code = s.code;
    try {
      await cancelMut.mutateAsync({ id: s.id, yalnizBossa: true });
      // Önce kapat (sorgu devre dışı kalır), SONRA tazele — silinmiş kaydı yeniden çekip 404 göstermesin.
      onClose();
      await qc.invalidateQueries();
      onDeleted?.(`${code} boş plan silindi.`);
    } catch (e) {
      const err = e as { data?: { code?: string } | null; message: string };
      await qc.invalidateQueries(); // bosPlan bayrağı sunucudaki gerçeğe dönsün
      if (err.data?.code === "PRECONDITION_FAILED") {
        cancelMut.reset(); // banner'da "hata" olarak kalmasın — neden pencerede not olarak görünür
        iptalPenceresiAc(err.message);
      }
      // Diğer hatalar cancelMut.error ile üstteki kırmızı şeritte görünür.
    } finally {
      setBusy(false);
      bosSilRef.current = false;
    }
  }

  /**
   * Sil düğmesinin TEK karar noktası:
   *  · dolu plan (sunucu: bosPlan=false)          → gerekçe penceresi (bugünkü akış)
   *  · kayıtta boş ama ekranda kaydedilmemiş girdi → gerekçe penceresi + NEDEN notu
   *  · gerçekten boş                               → onaysız sil (karar yine sunucunun DELETE'inde)
   */
  function silTiklandi() {
    if (!s) return;
    if (!s.bosPlan) { iptalPenceresiAc(); return; }
    if (kaydedilmemis) {
      iptalPenceresiAc(`Kaydedilmemiş girdi var (${kaydedilmemis}). Plan kayıtta boş görünse de bu bilgi de silinecek — silmek için gerekçe yazın.`);
      return;
    }
    void bosPlaniSil();
  }

  return (
    <>
    {/* widthPx 1404 = eski xxl (936) + %50 — sol adım rayı eklendiği için içerik genişliği korunur.
        MOBİLDE widthPx VERİLMEZ: inline genişlik, Drawer.css'in ≤767.98px `width:100vw` kuralını ezip
        kenarda backdrop şeridi bırakır (yanlışlıkla dokunma = kapanma). dismissible=false: wizard artık
        çok adımda veri biriktiriyor, backdrop tıklamasıyla kaydedilmemiş girdi uçmasın (11 drawer'daki desen). */}
    <Drawer
      open={open}
      onClose={onClose}
      size="xxl"
      widthPx={isMobile ? undefined : 1404}
      dismissible={false}
      title={s ? `Sevkiyat ${s.code}` : "Sevkiyat"}
      subtitle={s ? (s.isManual ? `Manuel · ${s.orders.length} sipariş` : `Sipariş ${s.orderCode} · ${s.customerName}`) : undefined}
      footer={s ? (
        <div className="shp-wiz-foot">
          <button type="button" className="btn-md secondary" disabled={stepIdx === 0} onClick={() => setStep(WIZ_STEPS[Math.max(0, stepIdx - 1)]!.key)}>
            <Icon name="arrowLeft" size={15} />Geri
          </button>
          <span className="shp-wiz-pos mono" aria-live="polite">{stepIdx + 1} / {WIZ_STEPS.length} · {WIZ_STEPS[stepIdx]?.label}</span>
          <button type="button" className="btn-md secondary" disabled={stepIdx >= WIZ_STEPS.length - 1} onClick={() => setStep(WIZ_STEPS[Math.min(WIZ_STEPS.length - 1, stepIdx + 1)]!.key)}>
            İleri<Icon name="arrowRight" size={15} />
          </button>
        </div>
      ) : undefined}
    >
      {!s ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>Yükleniyor…</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <span className={`badge-pill ${sb?.cls ?? ""}`}>{sb?.label}</span>
            {s.shippedAt ? <span className="mono" style={{ fontSize: 12, color: "var(--neutral-500)" }}>{fmtDate(s.shippedAt)}</span> : null}
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
            {s.status === "draft" ? (
              <button
                type="button"
                className="btn-md primary"
                // Zorunlu alanlar (KAYDEDİLMİŞ veride) eksikse onay kapalı — kullanıcı kararı: "onay engellensin".
                disabled={busy || confirmMut.isPending || preflighting || effLineCount === 0 || missingAll.length > 0}
                onClick={() => void startConfirm()}
              >
                <Icon name="truck" size={15} />{confirmMut.isPending ? "Onaylanıyor…" : preflighting ? "Lotlar kontrol ediliyor…" : "Sevkiyatı Onayla (stok çıkışı)"}
              </button>
            ) : null}
            <button type="button" className="btn-md secondary" onClick={() => printIrsaliye(s, profQ.data)}><Icon name="printer" size={15} />Yükleme Formu</button>
            {s.status === "draft" ? (
              // Boş plan: tek tık, onay/gerekçe YOK (metin bunu söyler). Dolu plan ya da kaydedilmemiş
              // girdi: gerekçe penceresi. Etiket/title/tıklama AYNI değerden (onaysizSilinebilir).
              <button type="button" className="btn-md secondary" style={{ color: "var(--danger)", marginLeft: "auto" }} disabled={busy}
                title={onaysizSilinebilir
                  ? "Plan boş (lot/şoför/evrak yok) — onay sorulmadan silinir"
                  : s.bosPlan ? `Kaydedilmemiş girdi var (${kaydedilmemis}) — silmek gerekçe ister` : undefined}
                onClick={silTiklandi}>
                {onaysizSilinebilir ? <><Icon name="trash" size={15} />Boş Planı Sil</> : <><Icon name="x" size={15} />İptal</>}
              </button>
            ) : null}
          </div>

          {(cancelErr || confirmMut.error || cancelMut.error) ? <div className="form-banner-err" role="alert">{cancelErr ?? (confirmMut.error || cancelMut.error)?.message}</div> : null}

          {/* Sevk tamamlandıysa: adımlardan BAĞIMSIZ tam künye kartı (Yükleme Formu butonunun hemen altında). */}
          {s.status === "confirmed" ? <ShipmentSummaryCard s={s} /> : null}

          {/* Durum = renk + ikon + METİN: onayı engelleyen eksik zorunlu alanlar açıkça listelenir. */}
          {missingAll.length > 0 ? (
            <div className="form-banner-err" role="alert">
              <Icon name="alertTriangle" size={14} />
              <span>Onay için eksik zorunlu alan{missingAll.length > 1 ? "lar" : ""}: <b>{missingAll.join(" · ")}</b> — ilgili adımı doldurup <b>Kaydet</b>'e basın.</span>
            </div>
          ) : null}

          {/* Sevke uygun olmayan lot ŞİMDİ söylenir — eskiden yalnız "Onayla"ya basınca çıkıyordu.
              Eksik-alan şeridiyle aynı desen: engel + nerede + ne yapılacak. */}
          {s.status === "draft" && blockedLineIds.size > 0 ? (
            <div className="form-banner-err" role="alert">
              <Icon name="alertTriangle" size={14} />
              <span>
                <b>{blockedLineIds.size}</b> satırdaki lot sevke uygun değil — bu haliyle onay <b>reddedilir</b>.
                <b> Yükleme Listesi</b> adımında işaretli satırların <b>Yükleme</b> anahtarını kapatın ya da lotunu değiştirin.
              </span>
            </div>
          ) : null}

          <div className="shp-wiz">
            <nav className="shp-wiz-nav" aria-label="Sevkiyat planı adımları">
              {WIZ_STEPS.map((st) => {
                const miss = stepMissing[st.key] ?? [];
                const act = st.key === step;
                // Yükleme Listesi adımı EKSİK ALAN değil, SEVKE UYGUN OLMAYAN LOT yüzünden de uyarır —
                // kullanıcı ray'a bakıp sorunun hangi adımda olduğunu görebilsin (eskiden hiçbir izi yoktu).
                const bad = st.key === "lines" && showReq ? blockedLineIds.size : 0;
                const n = miss.length + bad;
                return (
                  <button
                    key={st.key}
                    type="button"
                    className={`shp-wiz-step${act ? " act" : ""}${n ? " miss" : ""}`}
                    aria-current={act ? "step" : undefined}
                    title={bad ? `${bad} satırda sevke uygun olmayan lot` : miss.length ? `Eksik zorunlu alan: ${miss.join(", ")}` : st.label}
                    onClick={() => setStep(st.key)}
                  >
                    <span className="ic"><Icon name={st.icon} size={15} /></span>
                    <span className="t">{st.label}</span>
                    {/* Durum = renk + İKON + METİN: üçgen-ünlem yanında eksik alan SAYISI da yazılır (role="img"
                        + aria-label; çıplak span'da aria-label geçersizdir). */}
                    {n ? (
                      <span className="w" role="img" aria-label={bad ? `${bad} satırda sevke uygun olmayan lot` : `${miss.length} eksik zorunlu alan: ${miss.join(", ")}`}>
                        <Icon name="alertTriangle" size={14} /><b>{n}</b>
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </nav>

            <div className="shp-wiz-main">
          <ShipmentEvrakCard s={s} step={step} showReq={!!showReq} onKaydedilmemis={setEvrakKirli} />

          <div className={step === "lines" ? undefined : "shp-hide"}>
          {s.customerAddress ? (
            <div className="d-card" style={{ marginBottom: 14 }}>
              <div className="d-card-head">
                <div className="left"><span className="t">Teslimat Adresi</span></div>
              </div>
              <div className="d-card-body tight" style={{ fontSize: 12.5 }}>{s.customerName} · {s.customerAddress}</div>
            </div>
          ) : null}

          {/* Manuel sevkiyat + taslak: elle lot atama builder'ı */}
          {s.isManual && s.status === "draft" ? <ManualLineBuilder shipmentId={s.id} onKaydedilmemis={setLotSecimKirli} /> : null}

          <div className="d-card">
            <div className="d-card-head">
              <div className="left">
                <span className="t">{s.isManual ? "Sevkiyat Satırları" : "Pick List"}</span>
                {/* Yüklenecek / toplam — hariç tutulanlar listede kalır ama sevke girmez. */}
                <span className="s">{effLineCount === s.lines.length ? `${s.lines.length} satır` : `${effLineCount} / ${s.lines.length} satır yüklenecek`}</span>
              </div>
              {/* Hızlı lot arama — 59 satırda aranan lotu bulup anahtarını çevirmek için. */}
              {s.status === "draft" && s.lines.length > 0 ? (
                <div className="shp-find">
                  <Icon name="search" size={13} />
                  <input
                    className="form-input" value={lineQ} onChange={(e) => setLineQ(e.target.value)}
                    placeholder="Lot / ürün / depo ara…" aria-label="Pick list içinde lot ara"
                  />
                  {lineQ ? (
                    <>
                      <span className="shp-find-n">{visibleLines.length} sonuç</span>
                      <button type="button" className="iact" aria-label="Aramayı temizle" title="Temizle" onClick={() => setLineQ("")}><Icon name="x" size={12} /></button>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="d-card-body flush">
              {opErr ? (
                <div className="form-banner-err shp-line-err" role="alert">
                  <Icon name="alertTriangle" size={14} />{opErr}
                  <button type="button" className="iact" aria-label="Hatayı kapat" onClick={() => setOpErr(null)}><Icon name="x" size={12} /></button>
                </div>
              ) : null}
              {s.lines.length === 0 ? (
                <div style={{ padding: "14px 16px", fontSize: 12.5, color: "var(--neutral-500)" }}>{s.isManual ? "Henüz satır yok — yukarıdan sipariş seçip üretilen lotları atayın." : "Satır yok."}</div>
              ) : visibleLines.length === 0 ? (
                <div style={{ padding: "14px 16px", fontSize: 12.5, color: "var(--neutral-500)" }}>“{lineQ}” ile eşleşen satır yok.</div>
              ) : (
              <div className="dt2-wrap">
                <table className="dt2">
                  <thead>
                    <tr>
                      {s.isManual ? <th style={{ width: 130 }}>Sipariş</th> : null}
                      <th>Ürün</th>
                      <th>Lot</th>
                      <th>Lokasyon</th>
                      <th className="num">Miktar</th>
                      {s.status === "draft" ? (
                        // Başlıkta TÜMÜNÜ YÜKLE / TÜMÜNÜ ÇIKAR — 59 satırda tek tek tıklamamak için.
                        // Kısmen seçiliyken switch "açık değil" görünür; tıklanınca hepsi yüklemeye alınır.
                        <th className="shp-load-th">
                          <ToggleSwitch
                            checked={allLoaded} disabled={busy || visibleLines.length === 0}
                            ariaLabel={allLoaded ? "Tüm satırları yüklemeden çıkar" : "Tüm satırları yüklemeye al"}
                            onChange={(v) => run(() => setAllMut.mutateAsync({ shipmentId: s.id, excluded: !v, ...(lq ? { lineIds: visibleLines.map((x) => x.id) } : {}) }))}
                          />
                          <span>Yükleme</span>
                        </th>
                      ) : null}
                      {s.isManual && s.status === "draft" ? <th style={{ width: 40 }} aria-label="Sil"></th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLines.map((l) => (
                      <tr key={l.id} className={[l.excluded ? "shp-line-out" : "", blockedLineIds.has(l.id) && !l.excluded ? "shp-line-bad" : ""].filter(Boolean).join(" ") || undefined}>
                        {s.isManual ? <td className="mono" style={{ fontSize: 11, color: "var(--info)" }}>{l.orderCode ?? "—"}</td> : null}
                        <td>
                          <span className="mono" style={{ fontSize: 11, color: "var(--neutral-500)", marginRight: 6 }}>{l.itemCode}</span>{l.itemName}
                        </td>
                        <td className="mono" style={{ fontSize: 12, fontWeight: 500 }}>
                          {l.lotCode}
                          {s.status === "draft" && !l.excluded ? (
                            <button type="button" className="iact shp-sub-open" aria-label={`${l.lotCode} lotunu değiştir`} title="Yüklenen lot farklıysa değiştir"
                              disabled={busy} onClick={() => setSubLineId(subLineId === l.id ? null : l.id)}>
                              <Icon name="shuffle" size={12} />
                            </button>
                          ) : null}
                          {/* SORUN SATIRDA YAZAR — renk TEK BAŞINA anlam taşımasın (CLAUDE.md ✅#5).
                              Metin sunucudaki `reconcileShipmentLines`ten gelir; UI kural türetmez. */}
                          {!l.excluded && issueByLine.has(l.id) ? (
                            <span className={issueByLine.get(l.id)!.blocking ? "shp-line-issue" : "shp-line-issue fix"}>
                              <Icon name={issueByLine.get(l.id)!.blocking ? "alertTriangle" : "info"} size={11} />
                              {issueByLine.get(l.id)!.blocking
                                ? `${issueByLine.get(l.id)!.detail} — yüklenemez`
                                : `${issueByLine.get(l.id)!.detail} (onayda düzeltilir)`}
                            </span>
                          ) : null}
                        </td>
                        <td className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{l.locationCode}</td>
                        <td className="cell-num" style={{ fontWeight: 600 }}>
                          {s.status === "draft" && !l.excluded ? (
                            <input
                              key={`${l.id}-${l.qty}`}
                              className="form-input shp-qty" type="number" min={0.0001} step="any" max={l.plannedQty || undefined}
                              defaultValue={l.qty} disabled={busy} aria-label={`${l.lotCode} yüklenecek miktar`}
                              onBlur={(e) => {
                                const v = Number(e.target.value);
                                if (!Number.isFinite(v) || v <= 0 || Math.abs(v - l.qty) < 0.0001) { e.target.value = String(l.qty); return; }
                                run(() => updateLineMut.mutateAsync({ lineId: l.id, qty: v }));
                              }}
                            />
                          ) : fmtQty(l.qty)} {l.uom}
                          {/* Planlanandan az yükleniyorsa farkı göster (kalanı sonraki sevkiyata). */}
                          {!l.excluded && l.plannedQty > l.qty + 0.0001 ? (
                            <span className="shp-qty-note" title="Planlanan miktar">/ {fmtQty(l.plannedQty)}</span>
                          ) : null}
                        </td>
                        {s.status === "draft" ? (
                          <td className="shp-load-cell">
                            <ToggleSwitch
                              checked={!l.excluded} disabled={busy}
                              ariaLabel={`${l.lotCode} bu sevkiyata yüklensin`}
                              onChange={(v) => run(() => updateLineMut.mutateAsync({ lineId: l.id, excluded: !v }))}
                            />
                          </td>
                        ) : null}
                        {s.isManual && s.status === "draft" ? (
                          <td><button type="button" className="iact danger" aria-label="Satırı sil" title="Satırı sil" disabled={busy} onClick={() => run(() => removeLineMut.mutateAsync({ lineId: l.id }))}><Icon name="trash" size={13} /></button></td>
                        ) : null}
                      </tr>
                    )).flatMap((row, i) => {
                      const l = visibleLines[i]!;
                      if (subLineId !== l.id) return [row];
                      // Lot ikamesi satırın ALTINDA açılır (tablo düzeni bozulmasın diye tam genişlik hücre).
                      const cols = 4 + (s.isManual ? 1 : 0) + (s.status === "draft" ? 1 : 0) + (s.isManual && s.status === "draft" ? 1 : 0);
                      return [row, (
                        <tr key={`${l.id}-sub`}>
                          <td colSpan={cols} className="shp-sub-cell">
                            <LotSubstitutePicker lineId={l.id} onCancel={() => setSubLineId(null)}
                              onDone={() => { setSubLineId(null); void qc.invalidateQueries(); }} />
                          </td>
                        </tr>
                      )];
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>
          </div>{/* /lines adımı */}
            </div>{/* /shp-wiz-main */}
          </div>{/* /shp-wiz */}
        </>
      )}
    </Drawer>
    <ConfirmDialog
      open={!!confirm}
      title={confirm?.title ?? ""}
      message={confirm?.message}
      tone={confirm?.tone ?? "primary"}
      confirmLabel={confirm?.confirmLabel ?? "Onayla"}
      cancelLabel="Vazgeç"
      onConfirm={() => { const c = confirm; setConfirm(null); c?.run(); }}
      onCancel={() => setConfirm(null)}
    />
    </>
  );
}
